'use client';

import { MagnifyingGlass } from '@phosphor-icons/react/dist/ssr';
import { useDebounce } from '@uidotdev/usehooks';
import { useRouter } from 'next/navigation';
import React from 'react';

import { Button } from '@skrym/shared/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@skrym/shared/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@skrym/shared/ui/dialog';
import { cn } from '@skrym/shared/utils/cn';

import { type SearchResult, searchDocs } from '@/lib/search';

interface SearchDialogContext {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SearchDialogContext = React.createContext<SearchDialogContext | null>(null);

export function SearchDialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);

  return (
    <SearchDialogContext.Provider value={{ open, setOpen }}>
      {children}
    </SearchDialogContext.Provider>
  );
}

export function useSearchDialog(): SearchDialogContext {
  const context = React.useContext(SearchDialogContext);
  if (!context) {
    throw new Error('useSearchDialog must be used within a SearchDialogProvider');
  }

  return context;
}

export function SearchDialog() {
  const router = useRouter();

  const { open, setOpen } = useSearchDialog();

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [setOpen]);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<SearchResult[]>([]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  React.useEffect(() => {
    const search = async () => {
      if (debouncedSearchTerm === '') {
        setSearchResults([]);
        return;
      }

      const results = await searchDocs(debouncedSearchTerm);
      setSearchResults(results);
    };

    search();
  }, [debouncedSearchTerm]);

  return (
    <Dialog open={open} onOpenChange={(newOpen) => setOpen(newOpen)}>
      <DialogTitle className="hidden">Search docs</DialogTitle>
      <DialogContent className="p-0 top-16 translate-y-0">
        <Command shouldFilter={false} loop>
          <CommandInput
            placeholder="Search..."
            value={searchTerm}
            onValueChange={(newSearchTerm) => setSearchTerm(newSearchTerm)}
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {searchResults.map((sr) => (
                <CommandItem
                  key={sr.pathname}
                  value={sr.pathname}
                  onSelect={() => {
                    router.push(sr.pathname);
                    setOpen(false);
                  }}
                >
                  <div className="flex flex-col w-full">
                    <div className="flex flex-row w-full gap-1 justify-between items-center">
                      <p className="text-base">{sr.title}</p>

                      <p className="text-muted-foreground text-xs font-semibold uppercase">
                        {sr.groupTitle}
                      </p>
                    </div>

                    <p className="text-muted-foreground text-sm">{sr.description}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

interface SearchInputProps extends Omit<React.HTMLAttributes<HTMLButtonElement>, 'color' | 'size'> {
  size?: 'icon' | 'input';
}

export function SearchInput(props: SearchInputProps) {
  const { className, onClick, size = 'input', ...rest } = props;
  const { setOpen } = useSearchDialog();

  if (size === 'icon') {
    return (
      <Button
        {...rest}
        icon
        variant="outline"
        onClick={(e) => {
          setOpen(true);
          onClick?.(e);
        }}
        className={cn('', className)}
      >
        <MagnifyingGlass weight="bold" />
      </Button>
    );
  }

  return (
    <button
      {...rest}
      onClick={(e) => {
        setOpen(true);
        onClick?.(e);
      }}
      className={cn(
        'flex items-center gap-2 rounded-md border border-input bg-background px-2 py-2',
        className,
      )}
    >
      <MagnifyingGlass weight="bold" />
      <span className="grow text-left text-sm pr-4">Search docs…</span>
      <div className="flex gap-0.5">
        <kbd className="font-mono border rounded-sm bg-muted text-sm px-[0.4rem]">⌘</kbd>
        <kbd className="font-mono border rounded-sm bg-muted text-sm px-[0.4rem]">K</kbd>
      </div>
    </button>
  );
}
